// apps/client/src/world/spaces/room.ts (M1-T16) — a house room (one space per room, D-35): the toy-room shell (M1-T14 interior.ts)
// papered and floored from the room's catalog surfaces, a doormat at every exit, and the placed furniture (fish as home tanks, spec
// §9.10) at their footprint centres. Rugs are a floor layer (D-12): you walk over them, never through anything else.
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { getEntry, getItem, type CatalogId, type FishEntry, type SurfaceEntry } from '@whiskerton/content';
import type { ItemId, ItemView, RoomView, Vec2 } from '@whiskerton/shared';
import { footprintOf, type Placed } from '@whiskerton/shared/rules/placement';
import { contactBlob } from '../../assets/blob';
import { buildTank } from '../../assets/builders/tank';
import { toyMaterial } from '../../characters/doll';
import type { SpaceScene, SpaceSceneDef } from '../../game/spaces';
import { buildRoomShell, type BuiltRoom, type RoomBuildOptions } from '../interior';

export interface RoomSpaceScene extends SpaceScene {
  readonly kind: 'room'; view: RoomView; room: BuiltRoom;
  addItem(item: ItemView): Promise<void>; updateItem(item: ItemView): void; removeItem(id: ItemId): void;
  setRoom(view: RoomView): void; objectFor(id: ItemId): THREE.Object3D | undefined; placed(): Placed[];
}
export const isRoomScene = (s: SpaceScene | null): s is RoomSpaceScene => !!s && (s as Partial<RoomSpaceScene>).kind === 'room';
const pattern = (id: CatalogId) => (getItem(id) as SurfaceEntry).pattern;
const isRug = (id: CatalogId) => { const e = getEntry(id); return e?.kind === 'furniture' && e.category === 'rug'; };
/** DESIGN.md toy room: the doormat wood. */
const MAT = '#C9A27E';
/** Where a walking resident's centre stays away from the walls (about the resident's radius; the open south edge lets you reach the door). */
const WALL = 0.35, FRONT = 0.1;

export async function buildRoomScene(view: RoomView, items: readonly ItemView[],
  deps: { model(id: CatalogId): Promise<THREE.Object3D>; tank(catalogId: CatalogId): THREE.Object3D; paint?: RoomBuildOptions['paint'] }): Promise<RoomSpaceScene> {
  const scene = new THREE.Scene(); scene.background = new THREE.Color('#F3E4C6');
  let current = view;
  const shell = (v: RoomView) => buildRoomShell({ w: v.w, h: v.h, wallpaper: pattern(v.wallpaper), floor: pattern(v.floor) }, { paint: deps.paint });
  let room = shell(view);
  scene.add(room.group);
  const furniture = new THREE.Group(); furniture.name = 'furniture'; scene.add(furniture);
  const mats = new THREE.Group(); mats.name = 'exits'; scene.add(mats);
  const data = new Map<ItemId, ItemView>(), objs = new Map<ItemId, THREE.Object3D>();
  // The shell already lays a doormat inside the south door (x = floor(w / 2), y = h − 1); every other exit gets its own.
  const drawExits = () => {
    for (const m of mats.children) (m as THREE.Mesh).geometry.dispose();
    mats.clear();
    for (const e of current.exits) {
      if (e.at.x === Math.floor(current.w / 2) && e.at.y === current.h - 1) continue;
      const mat = new THREE.Mesh(new RoundedBoxGeometry(1.2, 0.04, 0.7, 2, 0.02), toyMaterial(MAT));
      mat.position.set(e.at.x + 0.5, 0.02, e.at.y + 0.5); mat.receiveShadow = true;
      if (e.at.x === 0) mat.rotation.y = Math.PI / 2;                 // the side-room door is in the left wall
      mats.add(mat);
    }
  };
  const place = (obj: THREE.Object3D, item: ItemView) => {
    if (item.at.loc !== 'room') return;
    const { w, d } = footprintOf({ catalogId: item.catalogId, rot: item.at.rot });
    obj.position.set(item.at.x + w / 2, isRug(item.catalogId) ? 0.004 : 0, item.at.y + d / 2);
    obj.rotation.y = -item.at.rot * Math.PI / 2;
  };
  const placedList = (): Placed[] => [...data.values()].flatMap((i) => (i.at.loc === 'room' ? [{ id: i.id, catalogId: i.catalogId, x: i.at.x, y: i.at.y, rot: i.at.rot }] : []));
  const walkable = (p: Vec2) => {
    if (p.x < WALL || p.y < WALL || p.x > current.w - WALL || p.y > current.h - FRONT) return false;
    return !placedList().some((q) => {
      if (isRug(q.catalogId)) return false;
      const { w, d } = footprintOf(q);
      return p.x >= q.x && p.x < q.x + w && p.y >= q.y && p.y < q.y + d;
    });
  };
  const self: RoomSpaceScene = {
    kind: 'room', view, room, scene,
    get bounds() { return { x: 0, y: 0, w: current.w, h: current.h }; },
    heightAt: () => 0,
    walkable,
    canMove: (_from, to) => walkable(to),
    tick: (_dt, minute) => room.light(minute),                     // light and look: the room follows the town clock
    async addItem(item) {
      if (item.at.loc !== 'room') return;
      const have = objs.get(item.id);
      if (have && data.get(item.id)?.catalogId === item.catalogId) { data.set(item.id, item); place(have, item); return; }   // already shown: move it
      self.removeItem(item.id);
      data.set(item.id, item);
      const obj = getEntry(item.catalogId)?.kind === 'fish' ? deps.tank(item.catalogId) : await deps.model(item.catalogId);
      if (data.get(item.id) !== item) return;                         // removed or replaced while the model loaded
      obj.userData.itemId = item.id; place(obj, item); objs.set(item.id, obj); furniture.add(obj);
    },
    updateItem(item) {
      if (item.at.loc !== 'room') { self.removeItem(item.id); return; }
      const o = objs.get(item.id); data.set(item.id, item);
      if (o) place(o, item); else void self.addItem(item);
    },
    removeItem(id) { data.delete(id); const o = objs.get(id); if (o) { furniture.remove(o); objs.delete(id); } },
    setRoom(v) {
      if (v.w !== current.w || v.h !== current.h) {                   // built overnight while you were inside (contracts rev 6 item 19)
        scene.remove(room.group); room.dispose();
        room = shell(v); self.room = room; scene.add(room.group);
      } else {
        if (v.wallpaper !== current.wallpaper) room.setWallpaper(pattern(v.wallpaper));
        if (v.floor !== current.floor) room.setFloor(pattern(v.floor));
      }
      current = v; self.view = v; drawExits();
    },
    objectFor: (id) => objs.get(id),
    placed: placedList,
    dispose: () => { room.dispose(); for (const m of mats.children) (m as THREE.Mesh).geometry.dispose(); },
  };
  drawExits();
  await Promise.all(items.map((i) => self.addItem(i)));
  return self;
}

/** A home tank (spec §9.10) on its contact blob (DESIGN.md "The Grounded Object Rule"). */
function tankOnBlob(id: CatalogId): THREE.Object3D {
  const g = buildTank(getItem(id) as FishEntry);
  const [w, d] = g.userData.footprint as [number, number];
  g.add(contactBlob(Math.max(w, d) * 1.2));
  return g;
}

export const space: SpaceSceneDef = {
  kind: 'room',
  async build(snap, game) {
    if (snap.kind !== 'room') throw new Error('room space expected');
    return buildRoomScene(snap.room, snap.items, { model: (id) => game.assets.model(id), tank: tankOnBlob });
  },
  apply(snap, scene) {
    if (snap.kind !== 'room' || !isRoomScene(scene)) return;
    scene.setRoom(snap.room);
    for (const p of scene.placed()) if (!snap.items.some((i) => i.id === p.id)) scene.removeItem(p.id as ItemId);
    for (const i of snap.items) void scene.addItem(i);
  },
};
